/**
 * AI Service - Unified AI generation service using the three-layer architecture
 * 
 * Frontend → User Backend → Admin Backend → External AI Providers
 * 
 * This service handles all AI generation requests through the secure backend,
 * without exposing any API keys to the frontend.
 */

import { getModelForCapability, getSelectedModels } from '../utils/modelHelper';

// CRITICAL: Get User Backend URL from localStorage (set during auth)
const getUserBackendUrl = () => {
    const storedApiUrl = localStorage.getItem('api_url');
    const baseUrl = storedApiUrl || 'https://app.loopai.in';
    return `${baseUrl}/api`;
};

// Common fetch wrapper with error handling
const apiCall = async (endpoint, options = {}, timeout = 120000) => {
    const apiUrl = getUserBackendUrl();
    const url = `${apiUrl}${endpoint}`;
    
    // Get auth credentials from localStorage
    const apiKey = localStorage.getItem('api_key');
    const storedUser = localStorage.getItem('user');
    const userEmail = storedUser ? JSON.parse(storedUser).email : null;
    
    const defaultOptions = {
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    };
    
    // Add auth headers
    if (apiKey) defaultOptions.headers['X-API-Key'] = apiKey;
    if (userEmail) defaultOptions.headers['X-User-Email'] = userEmail;
    
    try {
        console.log(`📡 API Call: ${options.method || 'GET'} ${url}`);
        
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            console.warn(`⏰ Request timeout after ${timeout}ms`);
            controller.abort();
        }, timeout);
        
        const fetchOptions = { 
            ...defaultOptions, 
            ...options,
            signal: controller.signal 
        };
        
        const response = await fetch(url, fetchOptions);
        clearTimeout(timeoutId);
        
        if (!response.ok) {
            let errorData = {};
            try {
                const text = await response.text();
                try {
                    errorData = JSON.parse(text);
                } catch {
                    errorData = { detail: text };
                }
            } catch (e) {
                // Response might not be readable
            }
            
            console.error(`❌ API Error: ${response.status}`, errorData);
            
            // Handle specific error codes
            if (response.status === 402) {
                throw new Error('INSUFFICIENT_CREDITS');
            }
            if (response.status === 401) {
                throw new Error('Not authenticated. Please log in from your dashboard first.');
            }
            if (response.status === 404) {
                throw new Error(`Endpoint not found: ${endpoint}. Please check if the backend API is properly configured.`);
            }
            if (response.status === 422) {
                // Validation error - show details
                console.error('❌ Validation Error (422) - Full details:', JSON.stringify(errorData, null, 2));
                const validationMsg = errorData.detail || 'Invalid request parameters';
                throw new Error(`Validation error: ${typeof validationMsg === 'string' ? validationMsg : JSON.stringify(validationMsg)}`);
            }
            if (response.status === 520) {
                throw new Error('Server error (520). The backend server may be overloaded. Please try again.');
            }
            if (response.status === 504 || response.status === 502) {
                throw new Error('Gateway timeout. The request took too long. Please try again.');
            }
            
            throw new Error(errorData.detail || errorData.message || `Request failed: ${response.status}`);
        }
        
        return response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            throw new Error('Request timed out. Please try again.');
        }
        
        // Network errors (CORS, offline, etc.)
        if (error.message === 'Failed to fetch') {
            console.error('❌ Network error - possible causes: CORS, offline, or server unreachable');
            throw new Error('Network error: Unable to reach the server. Please check your connection and try again.');
        }
        
        throw error;
    }
};

// ==================== VIDEO PRODUCTION PROMPT GENERATION ====================

/**
 * Generate detailed video production prompts from the backend
 * This provides cinematic prompts, camera settings, and action timelines for shot pre-production
 * 
 * @param {object} params - Parameters for prompt generation
 * @param {string} params.shotDescription - Description of the shot
 * @param {string} params.characterName - Name of the main character in the shot
 * @param {string} params.sceneContext - Context/description of the scene
 * @param {number} params.durationSeconds - Duration of the shot in seconds
 * @param {string} params.aspectRatio - Aspect ratio (e.g., "16:9", "9:16", "1:1")
 * @param {string} params.mood - Mood of the shot
 * @param {string} params.genre - Genre of the project
 * @returns {Promise<object>} - Production prompt with video_prompt, camera, character_actions
 */
export const generateVideoProductionPrompt = async ({
    shotDescription,
    characterName,
    sceneContext,
    durationSeconds,
    aspectRatio = '16:9',
    mood,
    genre
}) => {
    try {
        console.log('🎬 Generating video production prompt...');
        console.log('  - Shot description:', shotDescription?.substring(0, 100) + '...');
        console.log('  - Character:', characterName);
        console.log('  - Duration:', durationSeconds, 'seconds');
        console.log('  - Mood:', mood);
        console.log('  - Genre:', genre);
        
        const result = await apiCall('/ai/generate-video-production-prompt', {
            method: 'POST',
            body: JSON.stringify({
                shot_description: shotDescription,
                character_name: characterName,
                scene_context: sceneContext,
                duration_seconds: durationSeconds,
                aspect_ratio: aspectRatio,
                mood: mood,
                genre: genre
            })
        });
        
        console.log('✅ Video production prompt generated successfully');
        console.log('📋 Full API response:', JSON.stringify(result, null, 2));
        console.log('  - Main prompt:', result.production_prompt?.video_prompt?.main_prompt?.substring(0, 100) + '...');
        console.log('  - Camera settings:', result.production_prompt?.camera);
        console.log('  - Actions:', JSON.stringify(result.production_prompt?.character_actions));
        console.log('  - Actions count:', result.production_prompt?.character_actions?.length);
        
        return result.production_prompt;
    } catch (error) {
        console.error('❌ Failed to generate video production prompt:', error);
        throw error;
    }
};

// ==================== VIDEO GENERATION ====================

/**
 * Poll for video job completion status
 * Video generation can take longer than images, so we use different defaults
 * @param {string} jobId - Job ID to poll for
 * @param {number} maxAttempts - Maximum polling attempts (default 120 = 10 minutes at 5s intervals)
 * @param {number} intervalMs - Polling interval in milliseconds (default 5000 = 5 seconds)
 * @param {function} onProgress - Optional callback for progress updates
 * @returns {Promise<object>} - Job result when completed
 */
const pollForVideoJobResult = async (jobId, maxAttempts = 120, intervalMs = 5000, onProgress = null) => {
    console.log(`🔄 Starting to poll for video job ${jobId}...`);
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const result = await apiCall(`/ai/jobs/${jobId}`, {
                method: 'GET'
            });
            
            console.log(`📊 Video poll attempt ${attempt}/${maxAttempts}: status = ${result.status}`);
            
            if (onProgress) {
                const progressPercent = result.progress || Math.round((attempt / maxAttempts) * 100);
                onProgress(`Processing video... ${progressPercent}% (attempt ${attempt}/${maxAttempts})`);
            }
            
            if (result.status === 'completed' || result.status === 'success') {
                console.log('✅ Video job completed successfully');
                return result;
            }
            
            if (result.status === 'failed' || result.status === 'error') {
                const errorMsg = result.error || result.message || 'Video generation failed';
                console.error('❌ Video job failed:', errorMsg);
                throw new Error(errorMsg);
            }
            
            // Job still processing, wait and retry
            await new Promise(resolve => setTimeout(resolve, intervalMs));
            
        } catch (error) {
            // If it's a polling error (not a job failure), log and continue
            if (!error.message.includes('failed') && !error.message.includes('error') && attempt < maxAttempts) {
                console.warn(`⚠️ Video poll error (attempt ${attempt}), retrying:`, error.message);
                await new Promise(resolve => setTimeout(resolve, intervalMs));
            } else {
                throw error;
            }
        }
    }
    
    throw new Error(`Video generation timed out after ${maxAttempts * intervalMs / 1000} seconds`);
};

/**
 * Parse video URL from various response formats
 */
function parseVideoUrl(result) {
    let videoUrl = null;
    
    // Deep nested: result.result.result.video_url
    if (result.result?.result?.video_url) {
        videoUrl = result.result.result.video_url;
    } else if (result.result?.result?.url) {
        videoUrl = result.result.result.url;
    }
    // Standard nested: result.result.video_url
    else if (result.result?.video_url) {
        videoUrl = result.result.video_url;
    } else if (result.result?.url) {
        videoUrl = result.result.url;
    }
    // Direct: result.video_url
    else if (result.video_url) {
        videoUrl = result.video_url;
    } else if (result.url) {
        videoUrl = result.url;
    }
    // Also check for 'output' field (some providers use this)
    else if (result.output?.video_url) {
        videoUrl = result.output.video_url;
    } else if (result.output?.url) {
        videoUrl = result.output.url;
    }
    // Check data field
    else if (result.data?.video_url) {
        videoUrl = result.data.video_url;
    } else if (result.data?.url) {
        videoUrl = result.data.url;
    }
    
    return videoUrl;
}

/**
 * Generate a video using AI models (with async polling support)
 * @param {string} prompt - Text prompt describing the video
 * @param {number} duration - Duration in seconds (5 or 10)
 * @param {string} style - Video style/model key
 * @param {string} aspectRatio - Aspect ratio (16:9, 9:16, 1:1)
 * @param {string} imageUrl - Optional start frame image URL
 * @param {string} endImageUrl - Optional end frame image URL
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<{success: boolean, videoUrl: string, jobId: string, creditsUsed: number}>}
 */
export const generateVideo = async (prompt, duration, style, aspectRatio, imageUrl = null, endImageUrl = null, onProgress = null, dialoguesData = null, cinematicActions = null) => {
    try {
        if (onProgress) onProgress('Submitting video generation request...');
        
        const requestBody = {
            prompt,
            duration,
            model_key: style,  // Backend expects model_key
            aspect_ratio: aspectRatio
        };
        
        // Only add image URLs if provided - use backend's expected parameter names
        if (imageUrl) requestBody.start_frame_image = imageUrl;
        if (endImageUrl) requestBody.end_frame_image = endImageUrl;
        
        // Add structured dialogues data for voice/character mapping
        // This helps Veo 3.1 and similar models assign correct voices to characters
        if (dialoguesData && dialoguesData.length > 0) {
            requestBody.dialogues = dialoguesData;
            console.log('🎭 Including structured dialogues:', JSON.stringify(dialoguesData, null, 2));
        }
        
        // Add cinematic actions with timecodes
        if (cinematicActions && cinematicActions.length > 0) {
            requestBody.cinematic_actions = cinematicActions;
            console.log('🎬 Including cinematic actions:', JSON.stringify(cinematicActions, null, 2));
        }
        
        console.log('🎬 Generating video via User Backend...', JSON.stringify(requestBody, null, 2));
        
        if (onProgress) onProgress('Initializing video generation (this may take several minutes)...');
        
        // Use longer timeout for initial request (2 minutes)
        const result = await apiCall('/ai/generate-video', {
            method: 'POST',
            body: JSON.stringify(requestBody)
        }, 120000);
        
        console.log('✅ Video generation initial response:', JSON.stringify(result, null, 2));
        
        // Check if this is an async job (job_id returned but no immediate video_url)
        const immediateVideoUrl = parseVideoUrl(result);
        const jobId = result.job_id || result.result?.job_id;
        
        if (jobId && !immediateVideoUrl) {
            // Async job - need to poll for result
            console.log(`🔄 Video generation is async, job_id: ${jobId}. Starting polling...`);
            
            if (onProgress) onProgress('Video generation started. Polling for result...');
            
            // Poll for video job completion (up to 10 minutes)
            const completedResult = await pollForVideoJobResult(jobId, 120, 5000, onProgress);
            
            const videoUrl = parseVideoUrl(completedResult);
            
            if (!videoUrl) {
                console.error('❌ No video URL in completed job result:', JSON.stringify(completedResult));
                throw new Error(`Video job completed but no URL returned: ${JSON.stringify(completedResult).substring(0, 300)}`);
            }
            
            console.log('✅ Video generated successfully via polling');
            console.log('  Job ID:', jobId);
            console.log('  Credits used:', completedResult.credits_used || result.credits_used);
            console.log('  Credits remaining:', completedResult.credits_remaining || result.credits_remaining);
            console.log('  Video URL:', videoUrl);
            
            return {
                success: true,
                videoUrl: videoUrl,
                jobId: jobId,
                creditsUsed: completedResult.credits_used || result.credits_used || 0,
                creditsRemaining: completedResult.credits_remaining || result.credits_remaining
            };
        }
        
        // Synchronous response - video URL returned immediately
        if (immediateVideoUrl) {
            console.log('✅ Video generated synchronously');
            console.log('  Job ID:', jobId);
            console.log('  Credits used:', result.credits_used);
            console.log('  Credits remaining:', result.credits_remaining);
            console.log('  Video URL:', immediateVideoUrl);
            
            return {
                success: true,
                videoUrl: immediateVideoUrl,
                jobId: jobId,
                creditsUsed: result.credits_used || 0,
                creditsRemaining: result.credits_remaining
            };
        }
        
        // No video URL and no job ID - something went wrong
        console.error('❌ No video URL or job ID in response:', JSON.stringify(result));
        throw new Error(`No video generated - backend returned: ${JSON.stringify(result).substring(0, 300)}`);
        
    } catch (error) {
        console.error('❌ Video generation failed:', error);
        
        if (error.message === 'INSUFFICIENT_CREDITS') {
            throw new Error('Not enough credits. Please purchase more credits from your dashboard.');
        }
        if (error.message === 'UNAUTHORIZED') {
            throw new Error('Not authenticated. Please log in from your dashboard.');
        }
        
        throw error;
    }
};

/**
 * Generate video with Kling model (via User Backend)
 */
export const generateVideoWithKling = async (prompt, imageUrl, endImageUrl, aspectRatio, duration, onProgress) => {
    return generateVideo(prompt, duration, 'kling', aspectRatio, imageUrl, endImageUrl, onProgress);
};

/**
 * Generate video with Wan 2.2 model (via User Backend)
 */
export const generateVideoWithWan = async (prompt, imageUrl, aspectRatio, resolution, onProgress) => {
    return generateVideo(prompt, 5, 'wan', aspectRatio, imageUrl, null, onProgress);
};

/**
 * Generate video with Sora model (via User Backend)
 */
export const generateVideoWithSora = async (prompt, imageUrl, onProgress, resolution) => {
    return generateVideo(prompt, 4, 'sora', '16:9', imageUrl, null, onProgress);
};

/**
 * Generate video with LTX model (via User Backend)
 */
export const generateVideoWithLTX = async (prompt, imageUrl, onProgress, resolution) => {
    return generateVideo(prompt, 5, 'ltx', '16:9', imageUrl, null, onProgress);
};

/**
 * Generate video with Minimax model (via User Backend)
 */
export const generateVideoWithMinimax = async (prompt, imageUrl, onProgress) => {
    return generateVideo(prompt, 5, 'minimax', '16:9', imageUrl, null, onProgress);
};

// ==================== IMAGE GENERATION ====================

/**
 * Poll for job completion status
 * @param {string} jobId - Job ID to poll for
 * @param {number} maxAttempts - Maximum polling attempts (default 60 = 5 minutes at 5s intervals)
 * @param {number} intervalMs - Polling interval in milliseconds (default 5000 = 5 seconds)
 * @param {function} onProgress - Optional callback for progress updates
 * @returns {Promise<object>} - Job result when completed
 */
const pollForJobResult = async (jobId, maxAttempts = 60, intervalMs = 5000, onProgress = null) => {
    console.log(`🔄 Starting to poll for job ${jobId}...`);
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const result = await apiCall(`/ai/jobs/${jobId}`, {
                method: 'GET'
            });
            
            console.log(`📊 Poll attempt ${attempt}/${maxAttempts}: status = ${result.status}`);
            
            if (onProgress) {
                onProgress({
                    attempt,
                    maxAttempts,
                    status: result.status,
                    progress: result.progress || Math.round((attempt / maxAttempts) * 100)
                });
            }
            
            if (result.status === 'completed' || result.status === 'success') {
                console.log('✅ Job completed successfully');
                return result;
            }
            
            if (result.status === 'failed' || result.status === 'error') {
                const errorMsg = result.error || result.message || 'Generation failed';
                console.error('❌ Job failed:', errorMsg);
                throw new Error(errorMsg);
            }
            
            // Job still processing, wait and retry
            await new Promise(resolve => setTimeout(resolve, intervalMs));
            
        } catch (error) {
            // If it's a polling error (not a job failure), log and continue
            if (!error.message.includes('failed') && attempt < maxAttempts) {
                console.warn(`⚠️ Poll error (attempt ${attempt}), retrying:`, error.message);
                await new Promise(resolve => setTimeout(resolve, intervalMs));
            } else {
                throw error;
            }
        }
    }
    
    throw new Error(`Job timed out after ${maxAttempts * intervalMs / 1000} seconds`);
};

/**
 * Generate images using AI models (with async polling for long-running jobs)
 * @param {string} prompt - Text prompt describing the image
 * @param {number} count - Number of images to generate
 * @param {string} style - Image style / model key
 * @param {string} aspectRatio - Aspect ratio
 * @param {string[]} referenceImages - Optional reference image URLs
 * @param {boolean} useAsync - Use async endpoint with polling (default true for reliability)
 * @param {function} onProgress - Optional callback for progress updates
 * @returns {Promise<{success: boolean, images: string[], jobId: string, creditsUsed: number}>}
 */
export const generateImage = async (prompt, count = 1, style = 'default', aspectRatio = '16:9', referenceImages = [], useAsync = true, onProgress = null) => {
    try {
        // Get the selected model from AI Toolkit, fallback to passed style or default
        let modelKey = style;
        
        // Get all selected models from localStorage for debugging
        const allSelectedModels = getSelectedModels();
        console.log('🔍 All selected models from localStorage:', allSelectedModels);
        
        // Try to get the user-selected model from localStorage
        const hasReferenceImages = referenceImages && referenceImages.length > 0;
        if (hasReferenceImages) {
            // For image-to-image generation - try multiple possible keys
            const possibleRefKeys = [
                'Image reference to image',  // Exact backend key
                'image_ref_to_image',         // Internal key
                'reference_image',            // Legacy key
            ];
            
            for (const key of possibleRefKeys) {
                const model = allSelectedModels[key] || getModelForCapability(key);
                if (model) {
                    modelKey = model;
                    console.log('🎨 Using selected image-ref-to-image model:', modelKey, 'from key:', key);
                    break;
                }
            }
        } else {
            // For text-to-image generation - try multiple possible keys
            const possibleTextKeys = [
                'Text to image',      // Exact backend key  
                'image_generation',   // Backend key for "Image Generation"
                'text_to_image',      // Internal key
                'professional_image', // Legacy key
            ];
            
            for (const key of possibleTextKeys) {
                const model = allSelectedModels[key] || getModelForCapability(key);
                if (model) {
                    modelKey = model;
                    console.log('🎨 Using selected text-to-image model:', modelKey, 'from key:', key);
                    break;
                }
            }
        }
        
        // Log if no model was found
        if (modelKey === style || modelKey === 'default') {
            console.warn('⚠️ No selected model found for image generation. Using default:', modelKey);
            console.warn('⚠️ Available keys in localStorage:', Object.keys(allSelectedModels));
        }
        
        // Format request according to User Backend API spec
        const requestBody = {
            prompt,
            count,
            model_key: modelKey,  // Use model_key for dynamic model selection
            model: modelKey,      // Fallback for backends expecting 'model'
            style: modelKey,      // Fallback for backends expecting 'style'
            aspect_ratio: aspectRatio,
            // Enable virtual try-on if:
            // 1. Prompt contains virtual try-on keywords
            // 2. Prompt contains costume reference markers
            // This ensures costume/accessory images are prioritized over text descriptions
            virtual_try_on: prompt.includes('virtual try-on') || 
                           prompt.includes('costume reference') || 
                           prompt.includes('[VIRTUAL_TRY_ON:ENABLED]') ||
                           prompt.includes('COSTUME INSTRUCTIONS')
        };
        
        // Only add reference_images if provided
        if (referenceImages && referenceImages.length > 0) {
            requestBody.reference_images = referenceImages;
        }
        
        // Use async endpoint with polling for long-running jobs (more reliable)
        if (useAsync) {
            console.log('🎨 Generating image via ASYNC endpoint...', JSON.stringify(requestBody, null, 2));
            
            // Start async generation
            const asyncResult = await apiCall('/ai/generate-image-async', {
                method: 'POST',
                body: JSON.stringify(requestBody)
            });
            
            console.log('📥 Async generation started:', asyncResult);
            
            const jobId = asyncResult.job_id;
            if (!jobId) {
                // Fallback: maybe the async endpoint returned the result directly
                if (asyncResult.images || asyncResult.result?.images || asyncResult.result?.image_url) {
                    console.log('✅ Async endpoint returned result directly');
                    return parseImageResult(asyncResult);
                }
                throw new Error('No job_id returned from async endpoint');
            }
            
            // Poll for result
            const estimatedTime = asyncResult.estimated_time_seconds || 120;
            console.log(`⏱️ Estimated time: ${estimatedTime} seconds`);
            
            if (onProgress) {
                onProgress({ status: 'processing', message: `Starting generation (est. ${estimatedTime}s)...` });
            }
            
            const result = await pollForJobResult(jobId, 60, 5000, onProgress);
            return parseImageResult(result);
        }
        
        // Fallback: Use synchronous endpoint
        console.log('🎨 Generating image via SYNC endpoint...', JSON.stringify(requestBody, null, 2));
        
        const result = await apiCall('/ai/generate-image', {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });
        
        console.log('✅ Image generation response:', JSON.stringify(result, null, 2));
        return parseImageResult(result);
        
    } catch (error) {
        console.error('❌ Image generation failed:', error);
        
        if (error.message === 'INSUFFICIENT_CREDITS') {
            throw new Error('Not enough credits. Please purchase more credits from your dashboard.');
        }
        if (error.message === 'UNAUTHORIZED') {
            throw new Error('Not authenticated. Please log in from your dashboard.');
        }
        
        throw error;
    }
};

/**
 * Parse image result from various response formats
 */
function parseImageResult(result) {
    // Check for nested errors first
    if (result.result?.success === false || result.result?.error) {
        const errorMessage = result.result?.error || 'AI service error';
        console.error('❌ Backend returned error:', errorMessage);
        throw new Error(errorMessage);
    }
    
    if (result.result?.result?.success === false || result.result?.result?.error) {
        const errorMessage = result.result?.result?.error || 'AI service error';
        console.error('❌ Backend returned nested error:', errorMessage);
        throw new Error(errorMessage);
    }
    
    // Handle various response formats from backend
    let images = [];
    
    // Deep nested structure: result.result.result.image_url (from Admin Backend)
    if (result.result?.result?.image_url) {
        images.push(result.result.result.image_url);
    }
    if (result.result?.result?.video_url) {
        // Sometimes video_url contains image URL (as seen in the response)
        images.push(result.result.result.video_url);
    }
    if (result.result?.result?.images && Array.isArray(result.result.result.images)) {
        images.push(...result.result.result.images);
    }
    
    // Standard nested: result.result.image_url
    if (result.result?.image_url) {
        images.push(result.result.image_url);
    }
    if (result.result?.images && Array.isArray(result.result.images)) {
        images.push(...result.result.images);
    }
    if (result.result?.url) {
        images.push(result.result.url);
    }
    
    // Direct on result
    if (result.images && Array.isArray(result.images)) {
        images.push(...result.images);
    }
    if (result.image_url) {
        images.push(result.image_url);
    }
    if (result.url) {
        images.push(result.url);
    }
    
    // Filter out any undefined/null/duplicate values
    images = [...new Set(images.filter(img => img && typeof img === 'string' && img.length > 10))];
    
    if (images.length === 0) {
        console.error('❌ No valid images in response. Full response:', JSON.stringify(result));
        throw new Error(`No image generated - backend returned: ${JSON.stringify(result).substring(0, 300)}`);
    }
    
    console.log('  Job ID:', result.job_id);
    console.log('  Credits used:', result.credits_used);
    console.log('  Credits remaining:', result.credits_remaining);
    console.log('  Images found:', images);
    
    return {
        success: true,
        images: images,
        jobId: result.job_id,
        creditsUsed: result.credits_used || 0,
        creditsRemaining: result.credits_remaining
    };
}

/**
 * Generate a scene/frame image
 */
export const generateSceneImage = async (prompt, aspectRatio, entities = [], referenceImages = [], modelId = 'default') => {
    // Build reference images list with proper priority:
    // 1. HIGHEST: Costume/accessory reference images (virtual try-on) for characters
    // 2. HIGH: Element/prop images (must be consistent throughout project)
    // 3. MEDIUM: Character portrait images (for face/likeness)
    // 4. LOWEST: Any additional reference images passed in
    
    const allReferenceImages = [];
    const entityInstructions = [];
    let hasCostumeReferences = false;
    let hasElementReferences = false;
    
    entities.forEach((entity, idx) => {
        const entityName = entity.name || `Entity ${idx + 1}`;
        const isElement = entity.category === 'element';
        const isCharacter = entity.category === 'character' || entity.category === 'narrator' || !entity.category;
        
        if (isElement) {
            // Handle "Other Elements" (props, locations, etc.)
            // These should be used consistently throughout the project
            
            // Check for element's main image (generated or uploaded)
            if (entity.image) {
                console.log(`🎭 Element "${entityName}": Using element image for visual consistency`);
                allReferenceImages.push(entity.image);
                entityInstructions.push(`The "${entityName}" MUST look EXACTLY like the reference image provided. Do not modify its appearance.`);
                hasElementReferences = true;
            }
            
            // Check for element's reference images (used during generation)
            if (entity.referenceImages && entity.referenceImages.length > 0) {
                console.log(`🎭 Element "${entityName}": Using ${entity.referenceImages.length} reference images`);
                allReferenceImages.push(...entity.referenceImages.filter(Boolean));
                hasElementReferences = true;
            }
            
            // Also check costumeAndAccessoryImages for elements (some elements might use this)
            if (entity.costumeAndAccessoryImages && entity.costumeAndAccessoryImages.length > 0) {
                console.log(`🎭 Element "${entityName}": Using ${entity.costumeAndAccessoryImages.length} accessory images`);
                allReferenceImages.push(...entity.costumeAndAccessoryImages.filter(Boolean));
                hasElementReferences = true;
            }
        } else if (isCharacter) {
            // Handle Characters and Narrators
            
            // Check if character has costume/accessory reference images (HIGHEST PRIORITY)
            if (entity.costumeAndAccessoryImages && entity.costumeAndAccessoryImages.length > 0) {
                console.log(`👗 ${entityName}: Using ${entity.costumeAndAccessoryImages.length} costume/accessory reference images (HIGHEST PRIORITY)`);
                // Add costume images first (they take priority)
                allReferenceImages.push(...entity.costumeAndAccessoryImages.filter(Boolean));
                entityInstructions.push(`${entityName} MUST wear EXACTLY the outfit/costume shown in the costume reference images, ignoring any clothing described in text.`);
                hasCostumeReferences = true;
            }
            
            // Add character portrait image (for face/likeness)
            if (entity.image) {
                console.log(`👤 ${entityName}: Using character portrait for face/likeness reference`);
                allReferenceImages.push(entity.image);
            }
            
            // Check for character's reference images (used during portrait generation)
            if (entity.referenceImages && entity.referenceImages.length > 0) {
                console.log(`👤 ${entityName}: Using ${entity.referenceImages.length} additional reference images`);
                allReferenceImages.push(...entity.referenceImages.filter(Boolean));
            }
        }
    });
    
    // Add any additional reference images passed in (LOWEST PRIORITY)
    if (referenceImages && referenceImages.length > 0) {
        allReferenceImages.push(...referenceImages.filter(Boolean));
    }
    
    // Enhance prompt with entity-specific instructions
    let enhancedPrompt = prompt;
    if (entityInstructions.length > 0) {
        const instructionType = hasCostumeReferences ? 'COSTUME & ELEMENT' : 'ELEMENT';
        enhancedPrompt = `${prompt}\n\nIMPORTANT ${instructionType} INSTRUCTIONS (HIGHEST PRIORITY - must match reference images exactly):\n${entityInstructions.join('\n')}\nAll referenced elements, props, and costumes must look EXACTLY like their reference images throughout all shots.`;
        console.log(`📋 Enhanced prompt with ${entityInstructions.length} entity instructions`);
    }
    
    console.log(`🎬 generateSceneImage: ${allReferenceImages.length} total reference images (costume: ${hasCostumeReferences}, elements: ${hasElementReferences})`);
    
    // Generate with appropriate flags
    const hasImportantReferences = hasCostumeReferences || hasElementReferences;
    return generateImageWithCostumeSupport(enhancedPrompt, 1, modelId, aspectRatio, allReferenceImages, hasImportantReferences);
};

/**
 * Generate image with explicit costume reference support
 * This ensures the backend knows to prioritize costume references
 */
const generateImageWithCostumeSupport = async (prompt, count, style, aspectRatio, referenceImages, hasCostumeReferences = false) => {
    // Call the main generateImage function but ensure virtual_try_on is set if we have costume refs
    const result = await generateImage(
        hasCostumeReferences ? `${prompt} [VIRTUAL_TRY_ON:ENABLED]` : prompt,
        count,
        style,
        aspectRatio,
        referenceImages,
        true, // useAsync
        null  // onProgress
    );
    return result;
};

/**
 * Edit an existing image
 */
export const editImage = async (baseImage, prompt, characters = [], referenceImages = [], maskImage = null, modelId = 'default') => {
    try {
        // Get the selected model for image editing
        let selectedModelKey = modelId;
        const selectedImageEditModel = getModelForCapability('image_ref_to_image') || 
                                       getModelForCapability('Image reference to image') ||
                                       getModelForCapability('image_editing');
        if (selectedImageEditModel && modelId === 'default') {
            selectedModelKey = selectedImageEditModel;
            console.log('🎨 Using selected image editing model:', selectedModelKey);
        }
        
        const result = await apiCall('/ai/edit-image', {
            method: 'POST',
            body: JSON.stringify({
                prompt,
                image_url: baseImage,
                mask_url: maskImage,
                reference_images: [...characters.map(c => c.image).filter(Boolean), ...referenceImages],
                model_key: selectedModelKey,
                style: selectedModelKey
            })
        });
        
        // Log the full response to debug
        console.log('🎨 Edit image API response:', JSON.stringify(result, null, 2));
        
        // Handle various response formats from the backend
        let editedImageUrl = null;
        
        // Try different paths to find the image URL
        if (result.result?.image_url) {
            editedImageUrl = result.result.image_url;
        } else if (result.image_url) {
            editedImageUrl = result.image_url;
        } else if (result.images && result.images.length > 0) {
            editedImageUrl = result.images[0];
        } else if (result.result?.images && result.result.images.length > 0) {
            editedImageUrl = result.result.images[0];
        } else if (result.data?.image_url) {
            editedImageUrl = result.data.image_url;
        } else if (result.output?.image_url) {
            editedImageUrl = result.output.image_url;
        }
        
        console.log('🎨 Extracted image URL:', editedImageUrl);
        
        return {
            success: !!editedImageUrl,
            imageUrl: editedImageUrl,
            images: editedImageUrl ? [editedImageUrl] : [],
            jobId: result.job_id,
            creditsUsed: result.credits_used
        };
        
    } catch (error) {
        console.error('❌ Image editing failed:', error);
        throw error;
    }
};

/**
 * Generate character portrait with virtual try-on support
 * @param {string} description - Character description
 * @param {Array} refImages - Face/likeness reference images
 * @param {Array} costumeImages - Costume/accessory reference images (for virtual try-on)
 * @param {string} makeup - Makeup/styling level
 * @param {string} aspectRatio - Output aspect ratio
 * @param {string} category - 'character', 'narrator', or 'element'
 * @param {string} modelId - AI model to use
 */
export const generateCharacterPortrait = async (description, refImages = [], costumeImages = [], makeup = '', aspectRatio = '1:1', category = 'character', modelId = 'default') => {
    // Build an enhanced prompt that instructs AI to use costume references for virtual try-on
    let enhancedPrompt = `${category} portrait: ${description}.`;
    
    // Add makeup/styling instruction
    if (makeup && makeup !== 'natural') {
        enhancedPrompt += ` Makeup/styling: ${makeup}.`;
    }
    
    // If costume images are provided, add virtual try-on instructions
    if (costumeImages && costumeImages.length > 0) {
        enhancedPrompt += ` IMPORTANT: Use the provided costume/accessory reference images for virtual try-on. The character MUST wear EXACTLY the outfit, clothing, and accessories shown in the costume reference images, regardless of any clothing described in the text. The costume reference images take priority over any text description of clothing.`;
    }
    
    // If face reference images are provided, add likeness instructions
    if (refImages && refImages.length > 0) {
        enhancedPrompt += ` Maintain the exact facial features, likeness, and identity from the face reference images.`;
    }
    
    console.log('🎨 Portrait generation prompt:', enhancedPrompt);
    console.log('📸 Face reference images:', refImages?.length || 0);
    console.log('👗 Costume reference images:', costumeImages?.length || 0);
    
    // Use dedicated portrait generation with separate image categories
    return generatePortraitWithTryOn(
        enhancedPrompt,
        refImages,
        costumeImages,
        aspectRatio,
        modelId
    );
};

/**
 * Generate portrait with virtual try-on - sends face and costume images separately
 */
const generatePortraitWithTryOn = async (prompt, faceImages = [], costumeImages = [], aspectRatio = '1:1', modelId = 'default') => {
    try {
        // Log the received aspectRatio to debug
        console.log('🖼️ generatePortraitWithTryOn received aspectRatio:', aspectRatio, '(type:', typeof aspectRatio, ')');
        
        // Ensure aspectRatio is valid, fallback to 16:9 if not
        const validAspectRatio = aspectRatio && aspectRatio !== '' ? aspectRatio : '16:9';
        console.log('🖼️ Using aspectRatio:', validAspectRatio);
        
        // Get the selected model from AI Toolkit
        const allSelectedModels = getSelectedModels();
        let modelKey = modelId;
        
        // Determine which model to use based on whether we have reference images
        const hasAnyReferences = (faceImages?.length > 0) || (costumeImages?.length > 0);
        
        if (hasAnyReferences) {
            // For image-to-image generation with references
            const possibleRefKeys = [
                'Image reference to image',
                'image_ref_to_image',
                'reference_image',
            ];
            
            for (const key of possibleRefKeys) {
                const model = allSelectedModels[key] || getModelForCapability(key);
                if (model) {
                    modelKey = model;
                    console.log('🎨 Using image-ref model for portrait:', modelKey);
                    break;
                }
            }
        } else {
            // For text-to-image generation
            const possibleTextKeys = [
                'Text to image',
                'image_generation',
                'text_to_image',
            ];
            
            for (const key of possibleTextKeys) {
                const model = allSelectedModels[key] || getModelForCapability(key);
                if (model) {
                    modelKey = model;
                    console.log('🎨 Using text-to-image model for portrait:', modelKey);
                    break;
                }
            }
        }
        
        // Build request body with separate image categories
        const requestBody = {
            prompt,
            count: 1,
            model_key: modelKey,
            model: modelKey,
            aspect_ratio: validAspectRatio,  // Use validated aspect ratio
            // Flag for virtual try-on mode (with underscore as expected by backend)
            virtual_try_on: costumeImages && costumeImages.length > 0,
            // Separate image categories for the backend
            face_reference_images: faceImages || [],
            costume_reference_images: costumeImages || [],
            // Also send combined for backwards compatibility
            reference_images: [...(costumeImages || []), ...(faceImages || [])]
        };
        
        console.log('🎨 Portrait request:', {
            prompt: prompt.substring(0, 100) + '...',
            model_key: modelKey,
            aspect_ratio: validAspectRatio,
            virtual_try_on: requestBody.virtual_try_on,
            face_images_count: faceImages?.length || 0,
            costume_images_count: costumeImages?.length || 0
        });
        
        const result = await apiCall('/ai/generate-image', {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });
        
        console.log('✅ Portrait generation response:', JSON.stringify(result, null, 2));
        
        // Check for nested errors
        if (result.result?.success === false || result.result?.error) {
            throw new Error(result.result?.error || 'AI service error');
        }
        if (result.result?.result?.success === false || result.result?.result?.error) {
            throw new Error(result.result?.result?.error || 'AI service error');
        }
        
        // Extract image URL from response
        let imageUrl = result.result?.result?.image_url ||
                       result.result?.result?.video_url ||
                       result.result?.image_url ||
                       result.result?.url ||
                       result.image_url ||
                       result.url;
        
        // Also check images array
        if (!imageUrl) {
            const images = result.result?.result?.images ||
                          result.result?.images ||
                          result.images;
            if (images && images.length > 0) {
                imageUrl = images[0];
            }
        }
        
        if (!imageUrl) {
            throw new Error('No image generated');
        }
        
        return {
            success: true,
            images: [imageUrl],
            jobId: result.job_id,
            creditsUsed: result.credits_used
        };
        
    } catch (error) {
        console.error('❌ Portrait generation failed:', error);
        throw error;
    }
};

// ==================== ELEMENT IMAGE GENERATION ====================

/**
 * Generate an image for an "Other Element" (prop, location, object)
 * This is separate from character portrait generation as elements don't have faces, costumes, or makeup
 * 
 * @param {string} description - Description of the element
 * @param {string[]} referenceImages - Reference images to guide the generation (what the element should look like)
 * @param {string} aspectRatio - Aspect ratio for the generated image
 * @param {string} modelId - Model ID to use for generation
 * @returns {Promise<{success: boolean, images: string[], jobId: string, creditsUsed: number}>}
 */
export const generateElementImage = async (description, referenceImages = [], aspectRatio = '1:1', modelId = 'default') => {
    try {
        // Build prompt specifically for element generation
        const enhancedPrompt = `Generate an image of: ${description}. IMPORTANT: This is an inanimate object/element/prop/location. Do NOT include any humans, people, animals, or living creatures in this image. Focus only on the object/element itself.${referenceImages.length > 0 ? ' The generated element MUST closely match the provided reference images in style, appearance, and details.' : ''}`;
        
        console.log('🎭 Element generation:');
        console.log('  - Description:', description.substring(0, 100) + '...');
        console.log('  - Reference images:', referenceImages.length);
        console.log('  - Aspect ratio:', aspectRatio);
        console.log('  - Model:', modelId);
        
        // Get appropriate model for image generation
        const allSelectedModels = getSelectedModels();
        let selectedModelKey = modelId;
        
        if (referenceImages.length > 0) {
            // Use image-reference model if we have references
            const refModelKeys = ['Image reference to image', 'image_ref_to_image', 'reference_image'];
            for (const key of refModelKeys) {
                const model = allSelectedModels[key] || getModelForCapability(key);
                if (model) {
                    selectedModelKey = model;
                    console.log('  - Using image-ref model:', selectedModelKey);
                    break;
                }
            }
        } else {
            // Use text-to-image model
            const textModelKeys = ['Text to image', 'image_generation', 'text_to_image'];
            for (const key of textModelKeys) {
                const model = allSelectedModels[key] || getModelForCapability(key);
                if (model) {
                    selectedModelKey = model;
                    console.log('  - Using text-to-image model:', selectedModelKey);
                    break;
                }
            }
        }
        
        // Use the standard generateImage function with reference images
        const result = await generateImage(
            enhancedPrompt,
            1,
            selectedModelKey,
            aspectRatio,
            referenceImages,
            true,  // useAsync
            null   // onProgress
        );
        
        console.log('✅ Element image generated successfully');
        return result;
        
    } catch (error) {
        console.error('❌ Element generation failed:', error);
        throw error;
    }
};

// ==================== AUDIO/TTS GENERATION ====================

/**
 * Generate speech from text using AI
 * @param {string} text - Text to convert to speech
 * @param {string} voiceId - Voice ID to use
 * @returns {Promise<{success: boolean, audioUrl: string, jobId: string, creditsUsed: number}>}
 */
export const generateSpeech = async (text, voiceId) => {
    try {
        const result = await apiCall('/ai/generate-speech', {
            method: 'POST',
            body: JSON.stringify({
                text,
                voice_id: voiceId
            })
        });
        
        return {
            success: true,
            audioUrl: result.result?.audio_url,
            audioData: result.result?.audio_data, // Base64 if provided
            jobId: result.job_id,
            creditsUsed: result.credits_used
        };
        
    } catch (error) {
        console.error('❌ Speech generation failed:', error);
        throw error;
    }
};

/**
 * Clone a voice from audio sample
 * @param {string} name - Name for the cloned voice
 * @param {string} audioData - Audio data (base64 or URL)
 * @returns {Promise<{success: boolean, voiceId: string}>}
 */
export const cloneVoice = async (name, audioData) => {
    try {
        const result = await apiCall('/ai/clone-voice', {
            method: 'POST',
            body: JSON.stringify({
                name,
                audio_data: audioData
            })
        });
        
        return {
            success: true,
            voiceId: result.result?.voice_id,
            jobId: result.job_id
        };
        
    } catch (error) {
        console.error('❌ Voice cloning failed:', error);
        throw error;
    }
};

/**
 * Get available voices
 */
export const getVoices = async () => {
    try {
        const result = await apiCall('/ai/voices', {
            method: 'GET'
        });
        
        return result.voices || [];
        
    } catch (error) {
        console.error('❌ Failed to get voices:', error);
        return [];
    }
};

// ==================== TEXT GENERATION (Scripts, Prompts) ====================

/**
 * Generate script from concept
 * @param {string} concept - The story concept/idea
 * @param {string} style - Audio style: dialogue/narration/both/none
 * @param {string} region - Target region/country
 * @param {string} language - Dialogue/narration language
 * @param {Object} projectDetails - Project details from setup (name, duration, aspectRatio)
 */
export const generateScript = async (concept, style, region, language, projectDetails = {}) => {
    try {
        // Build request body with parameters the backend expects
        // Based on backend API: concept, duration, region, language, audio_style, style, tone
        const requestBody = {
            concept,
            style,
            region,
            language
        };
        
        // Add duration from project settings
        // Backend may expect duration as a number (seconds) or string
        if (projectDetails.duration) {
            let duration = projectDetails.duration;
            
            // Parse duration to extract numeric value
            // Handles formats: "< 15 seconds", "15 seconds", "1-3 minutes", etc.
            if (duration.includes('seconds')) {
                // Extract number from "< 15 seconds" or "15 seconds"
                const match = duration.match(/(\d+)\s*seconds/i);
                if (match) {
                    requestBody.duration = parseInt(match[1], 10); // Send as number
                }
            } else if (duration.includes('minute')) {
                // Extract from "< 1 minute", "1-3 minutes", etc.
                const match = duration.match(/(\d+)/);
                if (match) {
                    requestBody.duration = parseInt(match[1], 10) * 60; // Convert to seconds
                }
            } else {
                // Fallback - try to parse as number
                const num = parseInt(duration, 10);
                if (!isNaN(num)) {
                    requestBody.duration = num;
                }
            }
        }
        
        // Add optional parameters if available
        if (projectDetails.audioStyle) {
            requestBody.audio_style = projectDetails.audioStyle;
        }
        if (projectDetails.tone) {
            requestBody.tone = projectDetails.tone;
        }
        
        console.log('📝 Script generation request:', JSON.stringify(requestBody, null, 2));
        
        const result = await apiCall('/ai/generate-script', {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });
        
        // Backend returns deeply nested structure: result.result.result.script
        const script = result?.result?.result?.script || result?.result?.script || result?.script || '';
        
        console.log('✅ Script generation response:', {
            hasResult: !!result,
            hasNestedResult: !!result?.result,
            hasDoubleNestedResult: !!result?.result?.result,
            scriptLength: script?.length || 0
        });
        
        return script;
        
    } catch (error) {
        console.error('❌ Script generation failed:', error);
        throw error;
    }
};

/**
 * Generate image concepts for ad creation
 * Sends concept options (purpose, industry, actors, logo, etc.) to the backend
 */
export const generateConcepts = async (concept, region, projectDetails = {}) => {
    try {
        const requestBody = {
            concept,
            region,
            generation_type: 'ad_concepts',
            need_actor: projectDetails.needActor || false,
            add_logo: projectDetails.addLogo || false,
            num_images: projectDetails.numImages || 4,
            purpose: projectDetails.purpose || 'Product Ads',
            industry: projectDetails.industry || 'Technology',
            aspect_ratio: projectDetails.aspectRatio || '16:9',
            project_name: projectDetails.name || ''
        };

        if (projectDetails.productImages && projectDetails.productImages.length > 0) {
            requestBody.product_images = projectDetails.productImages;
        }

        console.log('📝 Concept generation request:', JSON.stringify({
            ...requestBody,
            product_images: requestBody.product_images 
                ? `[${requestBody.product_images.length} images, sizes: ${requestBody.product_images.map(img => `${Math.round(img.length / 1024)}KB`).join(', ')}]` 
                : 'none'
        }, null, 2));
        console.log('📦 Request body size:', Math.round(JSON.stringify(requestBody).length / 1024), 'KB');

        const result = await apiCall('/ai/generate-adconcepts', {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });

        const script = result?.result?.result?.script || result?.result?.script || result?.script || 
                       result?.result?.result?.concepts || result?.result?.concepts || result?.concepts || '';

        console.log('✅ Concept generation response:', {
            hasResult: !!result,
            resultLength: typeof script === 'string' ? script.length : JSON.stringify(script).length
        });

        return typeof script === 'string' ? script : JSON.stringify(script, null, 2);

    } catch (error) {
        console.error('❌ Concept generation failed:', error);
        throw error;
    }
};


/**
 * Parse characters and elements from markdown/text format
 * Handles Gemini's text-based descriptions for both characters and elements
 */
const parseCharactersFromText = (text) => {
    const items = [];
    
    console.log('🔍 Parsing text, length:', text?.length, 'Preview:', text?.substring(0, 300));
    
    if (!text || typeof text !== 'string') {
        console.warn('⚠️ No valid text to parse');
        return items;
    }
    
    /**
     * Helper function to determine the correct category for an item
     * @param {Object} item - The item with name and description
     * @returns {string} The correct category: 'character', 'narrator', or 'element'
     */
    const determineCategory = (item) => {
        const lowerName = (item.name || '').toLowerCase();
        const lowerDescription = (item.description || '').toLowerCase();
        
        // Body parts should be filtered out completely - return null to signal skip
        const bodyPartPatterns = [
            /\b(hand|hands|finger|fingers|arm|arms|leg|legs|foot|feet|eye|eyes|ear|ears|nose|mouth|lips|teeth|hair|head|face|neck|shoulder|shoulders|chest|back|stomach|knee|knees|elbow|elbows|wrist|wrists|ankle|ankles|thumb|thumbs|toe|toes|skin|nail|nails|palm|palms|forehead|chin|cheek|cheeks|eyebrow|eyebrows|eyelash|eyelashes|beard|mustache|torso|hip|hips|thigh|thighs|calf|calves)\b/i,
            /\b(\w+)'s\s+(hand|hands|finger|fingers|arm|arms|leg|legs|foot|feet|eye|eyes|ear|ears|face|hair|head|body|skin)\b/i
        ];
        
        if (bodyPartPatterns.some(pattern => pattern.test(lowerName))) {
            return null; // Signal to skip this item
        }
        
        // Check for narrator - flexible matching
        const narratorNamePatterns = [
            /^narrator\b/i,                    // Starts with "Narrator"
            /\bnarrator\b/i,                   // Contains "narrator" as a word
            /^voice\s*over\b/i,                // Starts with "Voice Over" or "Voiceover"
            /^vo\b/i,                          // Starts with "VO"
            /\bstoryteller\b/i                 // Contains "storyteller"
        ];
        
        const narratorDescPatterns = [
            /\bnarrator\b/i,
            /\bomniscient\b/i,
            /\bvoice\s*over\b/i,
            /\bnarration\b/i,
            /\bstoryteller\b/i,
            /\bnarrates\b/i
        ];
        
        const isNarratorByName = narratorNamePatterns.some(pattern => pattern.test(lowerName));
        const isNarratorByDesc = narratorDescPatterns.some(pattern => pattern.test(lowerDescription));
        
        if (isNarratorByName || isNarratorByDesc) {
            console.log('🎙️ Detected narrator:', item.name, { byName: isNarratorByName, byDesc: isNarratorByDesc });
            return 'narrator';
        }
        
        // Check for elements/props/locations
        const elementPatterns = /\b(logo|brand\s*logo|watermark|sink|cloth|lamp|shrine|idol|bell|mist|fog|rain|temple|stone|flame|moss|camera|shot|angle|light|sound|music|building|car|vehicle|tree|forest|city|room|house|office|door|window|table|chair|desk|phone|computer|book|letter|key|mirror|clock|painting|photograph|ring|necklace|sword|gun|knife)\b/i;
        
        if (elementPatterns.test(lowerName)) {
            return 'element';
        }
        
        // Default to keeping the original category or 'character'
        return item.category || 'character';
    };
    
    // First, try to parse as JSON
    try {
        const jsonData = JSON.parse(text);
        if (jsonData.characters && Array.isArray(jsonData.characters)) {
            console.log('✅ Parsed as JSON, found characters array with', jsonData.characters.length, 'items');
            // Apply category correction to each item - ALWAYS recategorize
            return jsonData.characters
                .map(char => {
                    const correctCategory = determineCategory(char);
                    if (correctCategory === null) {
                        console.log('⏭️ Skipping body part from JSON:', char.name);
                        return null;
                    }
                    // Log if we're changing the category
                    if (char.category && char.category !== correctCategory) {
                        console.log(`🔄 Recategorizing "${char.name}" from "${char.category}" to "${correctCategory}"`);
                    }
                    return {
                        ...char,
                        category: correctCategory
                    };
                })
                .filter(Boolean); // Remove null items (body parts)
        } else if (jsonData.result?.characters && Array.isArray(jsonData.result.characters)) {
            console.log('✅ Parsed as JSON, found result.characters array with', jsonData.result.characters.length, 'items');
            return jsonData.result.characters
                .map(char => {
                    const correctCategory = determineCategory(char);
                    if (correctCategory === null) {
                        console.log('⏭️ Skipping body part from JSON:', char.name);
                        return null;
                    }
                    // Log if we're changing the category
                    if (char.category && char.category !== correctCategory) {
                        console.log(`🔄 Recategorizing "${char.name}" from "${char.category}" to "${correctCategory}"`);
                    }
                    return {
                        ...char,
                        category: correctCategory
                    };
                })
                .filter(Boolean);
        }
    } catch (e) {
        console.log('🔄 Not JSON format, continuing with markdown parsing...');
    }
    
    // Continue with markdown parsing if not JSON
    // Look for CHARACTER sections in the script with various formats
    
    // First, try to find ## CHARACTER sections (format from backend)
    const characterSections = text.match(/##\s*CHARACTER\s*\d*[:\s]+([^\n]+)[\s\S]*?(?=##\s*(?:CHARACTER|NARRATOR|KEY\s*ELEMENT|SCENE|$))/gi);
    const narratorSections = text.match(/##\s*NARRATOR[:\s]*([^\n]*)[\s\S]*?(?=##\s*(?:CHARACTER|KEY\s*ELEMENT|SCENE|$))/gi);
    const elementSections = text.match(/##\s*KEY\s*ELEMENT[S]?[:\s]*[\s\S]*?(?=##\s*(?:CHARACTER|NARRATOR|SCENE|$))/gi);
    
    console.log('📋 Found sections:', {
        characters: characterSections?.length || 0,
        narrators: narratorSections?.length || 0,
        elements: elementSections?.length || 0
    });
    
    // Process CHARACTER sections
    if (characterSections) {
        for (const section of characterSections) {
            console.log('📄 Processing CHARACTER section:', section.substring(0, 150));
            
            // Extract name from header like "## CHARACTER 1: AYESHA"
            const headerMatch = section.match(/##\s*CHARACTER\s*\d*[:\s]+([^\n]+)/i);
            if (!headerMatch) continue;
            
            const name = headerMatch[1].trim();
            console.log('✓ Found character:', name);
            
            // Extract description from **Physical Description:** field
            const physDescMatch = section.match(/\*\*Physical\s*Description[:\s]*\*\*\s*([^\n]+(?:\n(?!\*\*)[^\n]+)*)/i);
            let description = physDescMatch ? physDescMatch[1].trim() : '';
            
            // Also try **Description:** format
            if (!description) {
                const descMatch = section.match(/\*\*Description[:\s]*\*\*\s*([^\n]+(?:\n(?!\*\*)[^\n]+)*)/i);
                description = descMatch ? descMatch[1].trim() : '';
            }
            
            // Fallback to first paragraph after header
            if (!description) {
                const lines = section.split('\n').slice(1).filter(l => l.trim() && !l.startsWith('##'));
                description = lines.slice(0, 3).join(' ').replace(/\*\*/g, '').trim();
            }
            
            const category = determineCategory({ name, description, category: 'character' });
            if (category === null) continue;
            
            items.push({
                name,
                description: description || `${name} - character from the scene`,
                category
            });
            console.log('✅ Added character:', { name, category, descLength: description.length });
        }
    }
    
    // Process NARRATOR sections
    if (narratorSections) {
        for (const section of narratorSections) {
            console.log('📄 Processing NARRATOR section:', section.substring(0, 150));
            
            // Extract narrator info
            const voiceMatch = section.match(/\*\*Voice\s*(?:Style|Description)[:\s]*\*\*\s*([^\n]+)/i);
            const description = voiceMatch ? voiceMatch[1].trim() : 'Narrator voice for the scene';
            
            items.push({
                name: 'Narrator',
                description,
                category: 'narrator'
            });
            console.log('✅ Added narrator');
        }
    }
    
    // Process KEY ELEMENTS sections
    if (elementSections) {
        for (const section of elementSections) {
            console.log('📄 Processing ELEMENTS section:', section.substring(0, 200));
            
            // Find individual elements - look for bullet points or numbered items
            const elementMatches = section.match(/(?:^|\n)\s*[-*•]\s*\*\*([^*]+)\*\*[:\s]*([^\n]+)/gm);
            
            if (elementMatches) {
                for (const match of elementMatches) {
                    const itemMatch = match.match(/[-*•]\s*\*\*([^*]+)\*\*[:\s]*(.+)/);
                    if (itemMatch) {
                        const name = itemMatch[1].trim();
                        const description = itemMatch[2].trim();
                        
                        const category = determineCategory({ name, description, category: 'element' });
                        if (category === null) continue;
                        
                        items.push({ name, description, category });
                        console.log('✅ Added element:', { name, category });
                    }
                }
            }
        }
    }
    
    // If no items found with structured parsing, try fallback methods
    if (items.length === 0) {
        console.log('🔄 No items from structured parsing, trying fallback patterns...');
        
        // Look for different section patterns - MORE FLEXIBLE matching
        // Split by double newlines or bold headers
        const sections = text.split(/\n\n+|\*\*(?:Character|Element|Prop|Location|Object|Item)[^*]*\*\*/gi);
        
        console.log('📋 Split into sections:', sections.length);
        
        // Also try splitting by numbered lists or bullet points
        const altSections = text.match(/(?:^|\n)(?:\d+\.|[-*])\s*\*\*[^*]+\*\*[\s\S]*?(?=(?:\n(?:\d+\.|[-*])\s*\*\*)|$)/g);
        
        if (altSections && altSections.length > sections.length) {
            console.log('📋 Using numbered list format, found:', altSections.length, 'items');
            sections.length = 0;
            sections.push(...altSections);
        }
        
        for (const section of sections) {
            if (!section.trim() || section.length < 10) continue;
            
            console.log('📄 Processing fallback section:', section.substring(0, 100));
            
            // Extract name from bold headers - MORE PATTERNS
            const nameMatch = section.match(/\*\*(?:Character Profile:|Character:|Element:|Prop:|Location:|Object:|Item:)?\s*([^*\n]+)\*\*/i);
            if (!nameMatch) {
                console.log('⚠️ No name found in section');
                continue;
            }
            
            const name = nameMatch[1].trim().replace(/["']/g, '');
            const content = section.substring(nameMatch[0].length).trim();
            
            console.log('✓ Found item:', name);
            
            // Extract description - try multiple patterns
            let description = '';
            
            // Look for Physical Description or Description field
            const descMatch = content.match(/\*\*(?:Physical Description|Description|Details|Appearance):\*\*\s*([^\*\n]+)/i);
            if (descMatch) {
                description = descMatch[1].trim();
            } else {
                // Use the first substantial paragraph
                const paragraphs = content.split('\n\n').filter(p => p.trim() && !p.match(/^\*\*\w+:\*\*/));
                if (paragraphs.length > 0) {
                    description = paragraphs[0].replace(/\*\*/g, '').trim();
                } else {
                    // Use first few lines
                    const lines = content.split('\n').filter(l => l.trim() && !l.match(/^\*\*\w+:\*\*/));
                    description = lines.slice(0, 3).join(' ').replace(/\*\*/g, '').trim();
                }
            }
            
            // If still no description, use the whole content
            if (!description || description.length < 10) {
                description = content.replace(/\*\*/g, '').replace(/\n+/g, ' ').trim().substring(0, 200);
            }
            
            // Use the determineCategory helper to get the correct category
            const itemForCategorization = { name, description, category: 'character' };
            
            // Check section header for additional context
            const lowerSection = section.toLowerCase();
            if (lowerSection.includes('**element') || 
                lowerSection.includes('**prop') || 
                lowerSection.includes('**location') || 
                lowerSection.includes('**object') ||
                lowerSection.includes('**setting') ||
                lowerSection.includes('**item') ||
                lowerSection.includes('**other element')) {
                itemForCategorization.category = 'element';
            } else if (lowerSection.includes('**narrator') || lowerSection.includes('narrator:')) {
                itemForCategorization.category = 'narrator';
            }
            
            const category = determineCategory(itemForCategorization);
            
            // Skip if determineCategory returns null (body parts)
            if (category === null) {
                console.log('⏭️ Skipping body part from markdown:', name);
                continue;
            }
            
            // Extract additional metadata if available
            const ageMatch = content.match(/\*\*Age:\*\*\s*(\d+)/i);
            const roleMatch = content.match(/\*\*Role:\*\*\s*([^\n*]+)/i);
            
            const item = {
                name: name,
                description: description || `${name} from the scene`,
                category: category
            };
            
            if (roleMatch) item.role = roleMatch[1].trim();
            if (ageMatch) item.age = parseInt(ageMatch[1]);
            
            items.push(item);
            console.log('✅ Added item:', { name, category, descLength: description.length });
        }
    }
    
    console.log(`📝 Parsed ${items.length} items from text (${items.filter(i => i.category === 'character').length} characters, ${items.filter(i => i.category === 'element').length} elements, ${items.filter(i => i.category === 'narrator').length} narrators)`);
    return items;
};

/**
 * Generate characters from script
 */
export const generateCharacters = async (script) => {
    try {
        const result = await apiCall('/ai/generate-characters', {
            method: 'POST',
            body: JSON.stringify({ script })
        });
        
        console.log('🔍 FULL Backend Response:', JSON.stringify(result, null, 2));
        
        // Try to extract string content from various possible locations
        let textContent = null;
        let allItems = null;
        
        // Check for text/string responses that need parsing
        if (typeof result?.result === 'string') {
            textContent = result.result;
        } else if (typeof result?.result?.result === 'string') {
            textContent = result.result.result;
        } else if (typeof result?.result?.text === 'string') {
            textContent = result.result.text;
        } else if (typeof result?.text === 'string') {
            textContent = result.text;
        }
        
        // If we found text content, parse it
        if (textContent) {
            console.log('🔄 Found text content, parsing markdown...');
            console.log('📝 Text preview:', textContent.substring(0, 500));
            allItems = parseCharactersFromText(textContent);
        } else {
            // Try to get array data from various locations
            allItems = result?.result?.items || 
                      result?.items ||
                      result?.result?.characters ||
                      result?.characters ||
                      result?.result?.data ||
                      result?.data ||
                      result?.result ||
                      result;
        }
        
        console.log('✅ Character generation raw response:', {
            hasResult: !!result,
            resultKeys: result ? Object.keys(result) : [],
            resultDotResult: result?.result ? Object.keys(result.result) : [],
            foundTextContent: !!textContent,
            responseType: typeof allItems,
            isArray: Array.isArray(allItems),
            isString: typeof allItems === 'string',
            itemsLength: Array.isArray(allItems) ? allItems.length : 'N/A',
            preview: typeof allItems === 'string' ? allItems.substring(0, 500) : (Array.isArray(allItems) ? `Array with ${allItems.length} items` : JSON.stringify(allItems).substring(0, 500))
        });
        
        // Handle string response (Gemini returns markdown/text)
        if (typeof allItems === 'string') {
            console.log('🔄 Parsing markdown character descriptions...');
            allItems = parseCharactersFromText(allItems);
        }
        
        // If still an object (not an array), try to parse it as text
        if (!Array.isArray(allItems) && typeof allItems === 'object' && allItems !== null) {
            console.warn('⚠️ Got object instead of array, checking for text fields...');
            
            // First, try to stringify and parse as JSON (backend might return nested JSON string)
            const objString = JSON.stringify(allItems);
            
            // Try to parse as JSON first
            try {
                const parsed = JSON.parse(objString);
                
                // Backend may return { characters: [], otherElements: [], narrators: [] }
                // We need to combine all of them
                let combinedItems = [];
                
                if (parsed.characters && Array.isArray(parsed.characters)) {
                    console.log(`✅ Found ${parsed.characters.length} characters in response`);
                    combinedItems = [...combinedItems, ...parsed.characters.map(c => ({ ...c, category: c.category || 'character' }))];
                }
                if (parsed.otherElements && Array.isArray(parsed.otherElements)) {
                    console.log(`✅ Found ${parsed.otherElements.length} otherElements in response`);
                    combinedItems = [...combinedItems, ...parsed.otherElements.map(e => ({ ...e, category: e.category || 'element' }))];
                }
                if (parsed.narrators && Array.isArray(parsed.narrators)) {
                    console.log(`✅ Found ${parsed.narrators.length} narrators in response`);
                    combinedItems = [...combinedItems, ...parsed.narrators.map(n => ({ ...n, category: n.category || 'narrator' }))];
                }
                if (parsed.elements && Array.isArray(parsed.elements)) {
                    console.log(`✅ Found ${parsed.elements.length} elements in response`);
                    combinedItems = [...combinedItems, ...parsed.elements.map(e => ({ ...e, category: e.category || 'element' }))];
                }
                
                // Also check nested result object
                if (parsed.result) {
                    if (parsed.result.characters && Array.isArray(parsed.result.characters)) {
                        console.log(`✅ Found ${parsed.result.characters.length} characters in result`);
                        combinedItems = [...combinedItems, ...parsed.result.characters.map(c => ({ ...c, category: c.category || 'character' }))];
                    }
                    if (parsed.result.otherElements && Array.isArray(parsed.result.otherElements)) {
                        console.log(`✅ Found ${parsed.result.otherElements.length} otherElements in result`);
                        combinedItems = [...combinedItems, ...parsed.result.otherElements.map(e => ({ ...e, category: e.category || 'element' }))];
                    }
                    if (parsed.result.narrators && Array.isArray(parsed.result.narrators)) {
                        console.log(`✅ Found ${parsed.result.narrators.length} narrators in result`);
                        combinedItems = [...combinedItems, ...parsed.result.narrators.map(n => ({ ...n, category: n.category || 'narrator' }))];
                    }
                }
                
                if (combinedItems.length > 0) {
                    console.log(`✅ Combined ${combinedItems.length} total items from backend response`);
                    allItems = combinedItems;
                } else {
                    // If object contains character data as text, parse as markdown
                    if (objString.includes('character') || objString.includes('Character') || objString.includes('CHARACTER')) {
                        console.log('🔄 Object contains character text data, attempting to parse as markdown...');
                        allItems = parseCharactersFromText(objString);
                    } else {
                        console.warn('⚠️ Object does not contain parseable character data');
                        allItems = [];
                    }
                }
            } catch (e) {
                console.log('🔄 Not valid JSON, trying markdown parsing...');
                if (objString.includes('character') || objString.includes('Character')) {
                    allItems = parseCharactersFromText(objString);
                } else {
                    allItems = [];
                }
            }
        }
        
        // Ensure we have an array
        if (!Array.isArray(allItems)) {
            console.warn('⚠️ Items is not an array, converting to empty array. Type:', typeof allItems);
            console.warn('⚠️ Actual value:', allItems);
            allItems = [];
        }
        
        // Check if we got any characters specifically
        const currentCharacters = allItems.filter(item => item.category === 'character');
        const currentElements = allItems.filter(item => item.category === 'element');
        
        console.log(`📊 Before fallback: ${currentCharacters.length} characters, ${currentElements.length} elements`);
        
        // FALLBACK: If backend returned no characters but we have a script, parse characters from script
        if (currentCharacters.length === 0 && script) {
            console.log('🔄 No characters from backend, attempting to parse from script...');
            const scriptParsedItems = parseCharactersFromText(script);
            const scriptCharacters = scriptParsedItems.filter(item => item.category === 'character');
            const scriptNarrators = scriptParsedItems.filter(item => item.category === 'narrator');
            
            if (scriptCharacters.length > 0) {
                console.log(`✅ Parsed ${scriptCharacters.length} characters from script!`);
                allItems = [...allItems, ...scriptCharacters];
            }
            if (scriptNarrators.length > 0 && !allItems.some(i => i.category === 'narrator')) {
                console.log(`✅ Parsed ${scriptNarrators.length} narrators from script!`);
                allItems = [...allItems, ...scriptNarrators];
            }
        }
        
        // IMPORTANT: Apply recategorization to ALL items to ensure correct narrator detection
        // This is needed because the backend might return items with incorrect categories
        console.log('🔄 Applying recategorization to all items...');
        allItems = allItems.map(item => {
            const lowerName = (item.name || '').toLowerCase();
            const lowerDescription = (item.description || '').toLowerCase();
            
            // Check for body parts to skip
            const bodyPartPatterns = [
                /\b(hand|hands|finger|fingers|arm|arms|leg|legs|foot|feet|eye|eyes|ear|ears|nose|mouth|lips|teeth|hair|head|face|neck|shoulder|shoulders|chest|back|stomach|knee|knees|elbow|elbows|wrist|wrists|ankle|ankles|thumb|thumbs|toe|toes|skin|nail|nails|palm|palms|forehead|chin|cheek|cheeks|eyebrow|eyebrows|eyelash|eyelashes|beard|mustache|torso|hip|hips|thigh|thighs|calf|calves)\b/i,
                /\b(\w+)'s\s+(hand|hands|finger|fingers|arm|arms|leg|legs|foot|feet|eye|eyes|ear|ears|face|hair|head|body|skin)\b/i
            ];
            
            if (bodyPartPatterns.some(pattern => pattern.test(lowerName))) {
                console.log('⏭️ Skipping body part:', item.name);
                return null;
            }
            
            // Check for narrator - this MUST take priority
            const narratorNamePatterns = [
                /^narrator\b/i,
                /\bnarrator\b/i,
                /^voice\s*over\b/i,
                /^vo\b/i,
                /\bstoryteller\b/i
            ];
            
            const narratorDescPatterns = [
                /\bnarrator\b/i,
                /\bomniscient\b/i,
                /\bvoice\s*over\b/i,
                /\bnarration\b/i,
                /\bstoryteller\b/i,
                /\bnarrates\b/i
            ];
            
            const isNarratorByName = narratorNamePatterns.some(pattern => pattern.test(lowerName));
            const isNarratorByDesc = narratorDescPatterns.some(pattern => pattern.test(lowerDescription));
            
            if (isNarratorByName || isNarratorByDesc) {
                if (item.category !== 'narrator') {
                    console.log(`🎙️ Recategorizing "${item.name}" to narrator (was: ${item.category || 'undefined'})`);
                }
                return { ...item, category: 'narrator' };
            }
            
            // Check for elements
            const elementPatterns = /\b(logo|brand\s*logo|watermark|sink|cloth|lamp|shrine|idol|bell|mist|fog|rain|temple|stone|flame|moss|camera|shot|angle|light|sound|music|building|car|vehicle|tree|forest|city|room|house|office|door|window|table|chair|desk|phone|computer|book|letter|key|mirror|clock|painting|photograph|ring|necklace|sword|gun|knife)\b/i;
            
            if (elementPatterns.test(lowerName)) {
                return { ...item, category: 'element' };
            }
            
            // Default to character if no category or keep existing
            return { ...item, category: item.category || 'character' };
        }).filter(Boolean); // Remove nulls (body parts)
        
        // Filter by category
        const characters = allItems.filter(item => item.category === 'character');
        const otherElements = allItems.filter(item => item.category === 'element');
        const narrators = allItems.filter(item => item.category === 'narrator');
        
        console.log('✅ Final parsed results:', {
            totalItems: allItems.length,
            characters: characters.length,
            otherElements: otherElements.length,
            narrators: narrators.length,
            charactersData: characters,
            otherElementsData: otherElements,
            narratorsData: narrators
        });
        
        // Return characters, other elements, and narrators
        return {
            characters: characters,
            otherElements: otherElements,
            narrators: narrators,
            allItems: allItems
        };
        
    } catch (error) {
        console.error('❌ Character generation failed:', error);
        throw error;
    }
};

/**
 * Generate storyboard from project
 */
export const generateStoryboard = async (project) => {
    try {
        const result = await apiCall('/ai/generate-storyboard', {
            method: 'POST',
            body: JSON.stringify({
                script: project.script,
                characters: project.characters
            })
        });
        
        // Backend guidance: Access via response.result.storyboard.scenes
        const storyboard = result?.result?.storyboard || result?.result || result?.storyboard || {};
        const scenes = storyboard?.scenes || [];
        
        console.log('✅ Storyboard generation response:', {
            hasResult: !!result,
            hasStoryboard: !!storyboard,
            scenesCount: scenes?.length || 0
        });
        
        return scenes;
        
    } catch (error) {
        console.error('❌ Storyboard generation failed:', error);
        throw error;
    }
};

/**
 * Generate frame prompts from shot description
 */
export const generateFramePrompts = async (description, shot) => {
    try {
        // If backend provides prompts, use them
        if (shot?.startFramePrompt && shot?.endFramePrompt) {
            return {
                startFramePrompt: shot.startFramePrompt,
                endFramePrompt: shot.endFramePrompt
            };
        }
        
        // Otherwise call the backend endpoint
        const result = await apiCall('/ai/generate-frame-prompts', {
            method: 'POST',
            body: JSON.stringify({ 
                description: description || shot?.description || 'A cinematic scene'
            })
        });
        
        // Backend guidance: Access via response.result.startFramePrompt / endFramePrompt
        return {
            startFramePrompt: result?.result?.startFramePrompt || `${description}, beginning of the scene`,
            endFramePrompt: result?.result?.endFramePrompt || `${description}, end of the scene`
        };
    } catch (error) {
        console.warn('⚠️ Frame prompt generation failed, using fallback:', error);
        // Fallback to local generation
        const basePrompt = description || 'A cinematic scene';
        return {
            startFramePrompt: `${basePrompt}, beginning of the scene`,
            endFramePrompt: `${basePrompt}, end of the scene`
        };
    }
};

/**
 * Generate video prompt for animation
 * This is generated locally since the backend endpoint may not exist
 */
export const generateVideoPrompt = async (shot, characters, script, duration) => {
    // Generate locally - backend endpoint may not be available
    const shotDesc = shot?.description || 'A cinematic scene';
    const characterNames = characters?.map(c => c.name).join(', ') || '';
    const actionDesc = characterNames ? `${characterNames} in ${shotDesc}` : shotDesc;
    
    return [
        { 
            startTime: "0.0s", 
            endTime: `${duration || 5}s`, 
            action: actionDesc 
        }
    ];
};

// ==================== CREDITS ====================

/**
 * Get current credits balance (quick check)
 */
export const getCreditsBalance = async () => {
    try {
        const result = await apiCall('/credits/balance', {
            method: 'GET'
        });
        
        return {
            credits: result.credits || 0,
            success: true
        };
        
    } catch (error) {
        console.error('❌ Failed to get credits balance:', error);
        return { credits: 0, success: false };
    }
};

/**
 * Get current credits balance (legacy - uses /credits endpoint)
 */
export const getCredits = async () => {
    try {
        const result = await apiCall('/credits', {
            method: 'GET'
        });
        
        return result.credits || 0;
        
    } catch (error) {
        console.error('❌ Failed to get credits:', error);
        // Try the balance endpoint as fallback
        try {
            const balanceResult = await getCreditsBalance();
            return balanceResult.credits;
        } catch (e) {
            return 0;
        }
    }
};

/**
 * Get project credit totals
 * @param {string} projectId - The project ID
 * @param {string[]} jobIds - Array of job IDs from the project
 * @returns {Promise<{total_credits_used: number, available_credits: number}>}
 */
export const getProjectCredits = async (projectId, jobIds = []) => {
    try {
        const result = await apiCall('/credits/project', {
            method: 'POST',
            body: JSON.stringify({
                project_id: projectId,
                job_ids: jobIds
            })
        });
        
        return {
            totalCreditsUsed: result.total_credits_used || 0,
            availableCredits: result.available_credits || 0,
            success: true
        };
        
    } catch (error) {
        console.error('❌ Failed to get project credits:', error);
        return { 
            totalCreditsUsed: 0, 
            availableCredits: 0, 
            success: false 
        };
    }
};

/**
 * Refresh credits display
 */
export const refreshCredits = async (setCreditsCallback) => {
    const credits = await getCredits();
    if (setCreditsCallback) {
        setCreditsCallback(credits);
    }
    return credits;
};

// ==================== AVAILABLE MODELS ====================

/**
 * Get available AI models from Admin Backend
 * Returns capabilities with their available models
 */
export const getAvailableModels = async () => {
    try {
        const result = await apiCall('/ai/available-models', {
            method: 'GET'
        });
        
        return result.capabilities || [];
        
    } catch (error) {
        console.error('❌ Failed to get available models:', error);
        return [];
    }
};

/**
 * Get models for a specific capability type
 * @param {string} taskType - Task type like 'video_generation', 'image_generation', 'tts', etc.
 */
export const getModelsForTask = async (taskType) => {
    try {
        const capabilities = await getAvailableModels();
        const capability = capabilities.find(c => c.task_type === taskType || c.key === taskType);
        return capability?.models || [];
    } catch (error) {
        console.error(`❌ Failed to get models for ${taskType}:`, error);
        return [];
    }
};

/**
 * Get Image-to-Video models (single image input)
 * Used when shot has only start frame OR only end frame
 */
export const getImageToVideoModels = async () => {
    try {
        const capabilities = await getAvailableModels();
        console.log('🎬 Looking for Image-to-Video models...');
        
        // Keys for single image to video
        const i2vKeys = [
            'Image to video',           // Backend exact key
            'image_to_video',            // Snake case variant
            'i2v',                       // Abbreviation
        ];
        
        for (const key of i2vKeys) {
            const capability = capabilities.find(c => 
                c.key === key || 
                c.task_type === key ||
                c.key?.toLowerCase() === key.toLowerCase() ||
                c.task_type?.toLowerCase() === key.toLowerCase()
            );
            if (capability?.models?.length > 0) {
                console.log('🎬 Found Image-to-Video models under key:', key, capability.models);
                return capability.models;
            }
        }
        
        console.warn('⚠️ No Image-to-Video models found');
        return [];
    } catch (error) {
        console.error('❌ Failed to get Image-to-Video models:', error);
        return [];
    }
};

/**
 * Get Start-End-Frame Video models (two images input: start + end frames)
 * Used when shot has BOTH start frame AND end frame
 */
export const getStartEndFrameVideoModels = async () => {
    try {
        const capabilities = await getAvailableModels();
        console.log('🎬 Looking for Start-End-Frame video models...');
        
        // Keys for start-end frame video generation
        const startEndKeys = [
            'start-end-frame videos',    // Backend exact key
            'start_end_frame_videos',    // Snake case variant
            'start-end-frame',           // Short variant
            'frame_interpolation',       // Alternative name
            'keyframe_video',            // Alternative name
        ];
        
        for (const key of startEndKeys) {
            const capability = capabilities.find(c => 
                c.key === key || 
                c.task_type === key ||
                c.key?.toLowerCase() === key.toLowerCase() ||
                c.task_type?.toLowerCase() === key.toLowerCase()
            );
            if (capability?.models?.length > 0) {
                console.log('🎬 Found Start-End-Frame video models under key:', key, capability.models);
                return capability.models;
            }
        }
        
        // Also check for capabilities that contain "start" and "end" or "frame" in their name
        const frameCapability = capabilities.find(c => 
            (c.key && (c.key.toLowerCase().includes('start') || c.key.toLowerCase().includes('frame'))) &&
            !c.key.toLowerCase().includes('image to video')
        );
        if (frameCapability?.models?.length > 0) {
            console.log('🎬 Found frame video models under capability:', frameCapability.key, frameCapability.models);
            return frameCapability.models;
        }
        
        console.warn('⚠️ No Start-End-Frame video models found');
        return [];
    } catch (error) {
        console.error('❌ Failed to get Start-End-Frame video models:', error);
        return [];
    }
};

/**
 * Get video generation models (Image to Video) - LEGACY function for backward compatibility
 * Tries multiple possible keys as backends may use different naming
 */
export const getVideoModels = async () => {
    try {
        const capabilities = await getAvailableModels();
        console.log('🎬 Looking for video models in capabilities:', capabilities.map(c => ({ key: c.key, task_type: c.task_type })));
        
        // Try multiple possible keys for video generation
        const videoKeys = [
            'Image to video',           // Backend exact key
            'image_to_video',            // Snake case variant
            'video_generation',          // Generic key
            'video',                     // Short key
            'i2v',                       // Abbreviation
        ];
        
        for (const key of videoKeys) {
            const capability = capabilities.find(c => 
                c.key === key || 
                c.task_type === key ||
                c.key?.toLowerCase() === key.toLowerCase() ||
                c.task_type?.toLowerCase() === key.toLowerCase()
            );
            if (capability?.models?.length > 0) {
                console.log('🎬 Found video models under key:', key, capability.models);
                return capability.models;
            }
        }
        
        // Also check for capabilities that contain "video" in their name/key
        const videoCapability = capabilities.find(c => 
            (c.key && c.key.toLowerCase().includes('video')) ||
            (c.task_type && c.task_type.toLowerCase().includes('video'))
        );
        if (videoCapability?.models?.length > 0) {
            console.log('🎬 Found video models under capability:', videoCapability.key, videoCapability.models);
            return videoCapability.models;
        }
        
        console.warn('⚠️ No video models found in capabilities');
        return [];
    } catch (error) {
        console.error('❌ Failed to get video models:', error);
        return [];
    }
};

/**
 * Get all video models (both Image-to-Video and Start-End-Frame) combined
 * Returns { imageToVideo: [], startEndFrame: [] }
 */
export const getAllVideoModels = async () => {
    const [imageToVideoModels, startEndFrameModels] = await Promise.all([
        getImageToVideoModels(),
        getStartEndFrameVideoModels()
    ]);
    
    return {
        imageToVideo: imageToVideoModels,
        startEndFrame: startEndFrameModels
    };
};

/**
 * Get image generation models
 */
export const getImageModels = async () => {
    return getModelsForTask('image_generation');
};

/**
 * Get TTS (text-to-speech) models
 */
export const getTTSModels = async () => {
    return getModelsForTask('tts');
};

/**
 * Get voice cloning models
 */
export const getVoiceCloningModels = async () => {
    return getModelsForTask('voice_cloning');
};

// ==================== LIPSYNC ====================

/**
 * Generate lipsync video
 */
export const generateLipsync = async (videoUrl, audioUrl, onProgress) => {
    try {
        if (onProgress) onProgress('Generating lipsync...');
        
        const result = await apiCall('/ai/generate-lipsync', {
            method: 'POST',
            body: JSON.stringify({
                video_url: videoUrl,
                audio_url: audioUrl
            })
        });
        
        return {
            success: true,
            videoUrl: result.result?.video_url,
            jobId: result.job_id,
            creditsUsed: result.credits_used
        };
        
    } catch (error) {
        console.error('❌ Lipsync generation failed:', error);
        throw error;
    }
};

// ==================== TRANSCRIPTION ====================

/**
 * Transcribe audio to text
 * Supports both JSON and FormData approaches for different backend implementations
 */
export const transcribeAudio = async (audioData) => {
    try {
        // Handle different audio data formats
        let audioBase64 = audioData;
        let mimeType = 'audio/webm';
        
        // If it's a data URL, extract the base64 part and mime type
        if (typeof audioData === 'string' && audioData.startsWith('data:')) {
            const matches = audioData.match(/^data:([^;]+);base64,(.+)$/);
            if (matches) {
                mimeType = matches[1];
                audioBase64 = matches[2];
            } else {
                // Just remove the data URL prefix if format doesn't match
                audioBase64 = audioData.split(',')[1] || audioData;
            }
        }
        
        console.log('🎤 Transcription request:', {
            hasAudioData: !!audioBase64,
            audioLength: audioBase64?.length || 0,
            mimeType: mimeType
        });
        
        // Convert base64 to blob for FormData upload
        const byteCharacters = atob(audioBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        
        // For webm recordings, try to convert to a more compatible format
        // Some backends may not support webm, so we'll indicate the format clearly
        let audioBlob = new Blob([byteArray], { type: mimeType });
        
        // Create FormData with the audio file
        const formData = new FormData();
        const fileExtension = mimeType.split('/')[1]?.split(';')[0] || 'webm';
        const fileName = `recording.${fileExtension}`;
        
        // Append file with 'file' field name (standard for most backends)
        formData.append('file', audioBlob, fileName);
        
        // Get auth headers
        const apiKey = localStorage.getItem('api_key');
        const storedUser = localStorage.getItem('user');
        const userEmail = storedUser ? JSON.parse(storedUser).email : null;
        
        const headers = {};
        if (apiKey) headers['X-API-Key'] = apiKey;
        if (userEmail) headers['X-User-Email'] = userEmail;
        
        console.log('🎤 Sending audio file via FormData...', { fileName, mimeType, blobSize: audioBlob.size });
        
        // Make the fetch request with proper error handling
        const response = await fetch(`${getUserBackendUrl()}/ai/transcribe`, {
            method: 'POST',
            headers: headers,
            body: formData
        });
        
        // Read response as text first (safest approach - read only once)
        let responseText = '';
        try {
            responseText = await response.text();
        } catch (readError) {
            console.error('❌ Failed to read response body:', readError);
            throw new Error(`Transcription failed: Could not read response (Status: ${response.status})`);
        }
        
        // Check if response was successful
        if (!response.ok) {
            console.error('❌ Transcription API error:', response.status, responseText);
            
            // Provide more helpful error messages
            if (response.status === 520) {
                throw new Error('Server error during transcription. The audio format may not be supported. Try uploading an MP3 or WAV file instead.');
            } else if (response.status === 413) {
                throw new Error('Audio file is too large. Please record a shorter clip or upload a smaller file.');
            } else if (response.status === 415) {
                throw new Error('Audio format not supported. Please try uploading an MP3 or WAV file.');
            }
            
            throw new Error(`Transcription failed: ${response.status} - ${responseText}`);
        }
        
        // Try to parse as JSON
        let responseData;
        try {
            responseData = JSON.parse(responseText);
        } catch (parseError) {
            // If not JSON, the text itself might be the transcription
            console.log('📄 Response is plain text:', responseText.substring(0, 100));
            if (responseText && responseText.trim()) {
                return responseText.trim();
            }
            throw new Error('Invalid response from transcription service');
        }
        
        console.log('✅ Transcription response:', responseData);
        
        // Extract the transcribed text from various possible response formats
        let transcribedText = '';
        
        // Check nested result.text first (most common format)
        if (responseData.result?.text && typeof responseData.result.text === 'string') {
            transcribedText = responseData.result.text;
        } else if (responseData.result?.transcript && typeof responseData.result.transcript === 'string') {
            transcribedText = responseData.result.transcript;
        } else if (responseData.result?.transcription && typeof responseData.result.transcription === 'string') {
            transcribedText = responseData.result.transcription;
        } else if (responseData.text && typeof responseData.text === 'string') {
            transcribedText = responseData.text;
        } else if (responseData.transcript && typeof responseData.transcript === 'string') {
            transcribedText = responseData.transcript;
        } else if (responseData.transcription && typeof responseData.transcription === 'string') {
            transcribedText = responseData.transcription;
        } else if (typeof responseData.result === 'string') {
            transcribedText = responseData.result;
        } else if (typeof responseData === 'string') {
            transcribedText = responseData;
        }
        
        // Validate we have actual text
        if (!transcribedText || typeof transcribedText !== 'string' || !transcribedText.trim()) {
            console.error('❌ No valid transcription text found in response:', responseData);
            throw new Error('Could not extract transcription text from response');
        }
        
        console.log('✅ Extracted transcription text:', transcribedText.substring(0, 100) + '...');
        return transcribedText;
        
    } catch (error) {
        console.error('❌ Transcription failed:', error);
        throw error;
    }
};

/**
 * Generate multiple ad images in a batch using the selected concept and reference images.
 * Calls the dedicated batch endpoint /api/ai/generate-adimages.
 * Falls back to individual image generation if the batch endpoint is unavailable.
 */
export const generateAdImages = async ({ conceptText, numImages, aspectRatio, referenceImages, imageVariations }, onProgress) => {
    // Resolve model key from AI Toolkit selection
    const allSelectedModels = getSelectedModels();
    let modelKey = 'default';
    const hasRefs = referenceImages && referenceImages.some(ref => ref.image);

    if (hasRefs) {
        for (const key of ['Image reference to image', 'image_ref_to_image', 'reference_image']) {
            const model = allSelectedModels[key] || getModelForCapability(key);
            if (model) { modelKey = model; break; }
        }
    } else {
        for (const key of ['Text to image', 'image_generation', 'text_to_image']) {
            const model = allSelectedModels[key] || getModelForCapability(key);
            if (model) { modelKey = model; break; }
        }
    }

    const requestBody = {
        concept_text: conceptText,
        num_images: numImages,
        aspect_ratio: aspectRatio,
        model_key: modelKey,
        reference_images: referenceImages,
        image_variations: imageVariations
    };

    try {
        if (onProgress) onProgress({ status: 'processing', message: 'Starting batch image generation...' });

        console.log('📤 Calling /ai/generate-adimages with:', JSON.stringify(requestBody, null, 2));
        
        const result = await apiCall('/ai/generate-adimages', {
            method: 'POST',
            body: JSON.stringify(requestBody)
        }, 300000);

        console.log('📥 /ai/generate-adimages response:', JSON.stringify(result, null, 2));

        // Handle async job
        const jobId = result.job_id;
        if (jobId && !(result.images?.length > 0)) {
            console.log('🔄 Async job detected, polling for job_id:', jobId);
            if (onProgress) onProgress({ status: 'processing', message: 'Generating images...' });
            const completed = await pollForJobResult(jobId, 120, 5000, onProgress);
            console.log('✅ Job completed:', JSON.stringify(completed, null, 2));
            return parseAdImagesResult(completed);
        }

        return parseAdImagesResult(result);
    } catch (error) {
        // Fallback: if batch endpoint not available OR network/CORS error, generate individually
        const errorMsg = error.message || '';
        const shouldFallback = errorMsg.includes('404') || 
                               errorMsg.includes('not found') || 
                               errorMsg.includes('Not Found') ||
                               errorMsg.includes('Network error') ||
                               errorMsg.includes('CORS') ||
                               errorMsg.includes('Failed to fetch') ||
                               errorMsg.includes('Unable to reach');
        
        if (shouldFallback) {
            console.warn('⚠️ Batch endpoint not available or network error, falling back to individual generation...');
            return await generateAdImagesFallback(requestBody, onProgress);
        }
        throw error;
    }
};

function parseAdImagesResult(result) {
    console.log('📥 parseAdImagesResult received:', JSON.stringify(result, null, 2));
    
    let images = [];

    const sources = [
        result?.result?.result?.images,
        result?.result?.images,
        result?.images,
        result?.result?.result?.image_urls,
        result?.result?.image_urls,
        result?.image_urls,
    ];

    console.log('🔍 Checking image sources:', sources.map((s, i) => `source[${i}]: ${Array.isArray(s) ? s.length + ' items' : typeof s}`));

    for (const source of sources) {
        if (Array.isArray(source) && source.length > 0) { images = source; break; }
    }

    // Check individual image fields
    if (images.length === 0) {
        for (let i = 1; i <= 10; i++) {
            const url = result?.result?.result?.[`image_${i}`] || result?.result?.[`image_${i}`] || result?.[`image_${i}`];
            if (url) images.push(url);
        }
    }

    // Also check for single image_url repeated
    if (images.length === 0) {
        const single = result?.result?.result?.image_url || result?.result?.image_url || result?.image_url;
        if (single) images.push(single);
    }

    images = [...new Set(images.filter(img => img && typeof img === 'string' && img.length > 10))];
    
    console.log('✅ Parsed images:', images.length, 'found');
    if (images.length === 0) {
        console.warn('⚠️ No images found in response. Full response structure:', Object.keys(result || {}));
    }

    return {
        success: images.length > 0,
        images,
        jobId: result?.job_id,
        creditsUsed: result?.credits_used || 0,
        creditsRemaining: result?.credits_remaining
    };
}

async function generateAdImagesFallback(params, onProgress) {
    const { concept_text, num_images, aspect_ratio, model_key, reference_images, image_variations } = params;

    const refImageUrls = (reference_images || [])
        .filter(ref => ref.image)
        .map(ref => ref.image);

    const results = [];
    for (let i = 0; i < num_images; i++) {
        if (onProgress) onProgress({ status: 'processing', message: `Generating image ${i + 1} of ${num_images}...` });

        const variation = image_variations?.[i]?.direction || '';
        const prompt = `${concept_text}\n\nImage direction for variation ${i + 1}: ${variation}`;

        try {
            const result = await generateImage(prompt, 1, model_key, aspect_ratio, refImageUrls, true, null);
            if (result.images?.[0]) results.push(result.images[0]);
        } catch (err) {
            console.error(`Image ${i + 1} generation failed:`, err);
        }
    }

    return { success: results.length > 0, images: results, creditsUsed: 0 };
}


// Export configuration helper
export const getConfig = () => ({
    userBackendUrl: getUserBackendUrl()
});
